/**
 * Central API configuration for RetailCLIQ ERP.
 *
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║  SINGLE SOURCE OF TRUTH — DO NOT BYPASS THIS FILE                  ║
 * ║                                                                      ║
 * ║  ALL API calls in the entire frontend MUST use API_BASE / API       ║
 * ║  exported from this file.                                            ║
 * ║                                                                      ║
 * ║  NEVER use process.env.REACT_APP_BACKEND_URL directly in any        ║
 * ║  component, screen, hook, context, or service.                       ║
 * ║  NEVER hardcode http:// or https:// URLs for backend calls.         ║
 * ║                                                                      ║
 * ║  HOW THE URL IS RESOLVED (priority order):                          ║
 * ║   1. process.env.REACT_APP_BACKEND_URL  (set in frontend/.env)      ║
 * ║   2. window.location.origin             (runtime fallback — works   ║
 * ║      in ANY deployment because K8s ingress routes /api/* to the     ║
 * ║      backend on the same domain automatically)                       ║
 * ║                                                                      ║
 * ║  This runtime fallback ensures preview builds, production builds,   ║
 * ║  and any custom-domain deployments all call the correct backend      ║
 * ║  WITHOUT requiring a rebuild.                                        ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

// Priority 1: build-time env var (set in frontend/.env)
// Priority 2: runtime window.location.origin (works on any domain automatically)
const _envUrl = process.env.REACT_APP_BACKEND_URL || '';
const _runtimeUrl = typeof window !== 'undefined' ? window.location.origin : '';


function resolveApiBase() {
  if (typeof window === "undefined") {
    // SSR safety - use env or default
    return _envUrl || "http://localhost:4200";
  }

  const { hostname } = window.location;

  // 1. CHECK: Are we on retailcliq.cloud domain? (PRODUCTION)
  if (hostname.includes('retailcliq.cloud')) {
    console.log('🔵 Production mode detected - using same-origin:', window.location.origin);
    return window.location.origin;
  }

  // 2. CHECK: Are we on localhost? (DEVELOPMENT)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Use env URL if set, otherwise default to localhost:4200
    const devUrl = _envUrl || 'http://localhost:4200';
    console.log('🟢 Development mode detected - using:', devUrl);
    return devUrl;
  }

  // 3. FALLBACK: If environment variable is set, use itsss
  if (_envUrl) {
    console.log('🟡 Using environment URL:', _envUrl);
    return _envUrl;
  }

  // 4. ULTIMATE FALLBACK: Same origin
  console.log('🟣 Fallback to same-origin:', window.location.origin);
  return window.location.origin;
}

export const API_BASE = resolveApiBase() || _envUrl || _runtimeUrl;

export const API = `${API_BASE}/api`;

// ── Dev-mode diagnostics ─────────────────────────────────────────────────────
if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
  if (!_envUrl) {
    console.warn(
      '[RetailCLIQ] REACT_APP_BACKEND_URL is not set in frontend/.env — ' +
      `falling back to runtime origin: ${_runtimeUrl}. ` +
      'Set the env var explicitly for consistent behaviour.'
    );
  }
  if (_envUrl && _runtimeUrl && _envUrl !== _runtimeUrl) {
    console.info(
      `[RetailCLIQ] API origin: ${_envUrl} (from env). ` +
      `Runtime origin is: ${_runtimeUrl}. ` +
      'They differ — this is fine in cross-origin dev setups.'
    );
  }
}

/**
 * getToken — reads the auth token from both storages so that
 * session-only logins (rememberMe=false, token in sessionStorage)
 * and persistent logins (rememberMe=true, token in localStorage) both work.
 */
export const getToken = () =>
  localStorage.getItem('retailcliq_token') ||
  sessionStorage.getItem('retailcliq_token') ||
  '';
